import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useParams, Link } from "react-router-dom";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { AlertTriangle, TrendingUp, ScrollText, MessageSquare, Download } from "lucide-react";
import { generateReportPDF } from "@/lib/pdf";
import { toast } from "sonner";

export default function SharedReport() {
  const { shareId } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/report/share/${shareId}`);
        setR(data);
      } catch { setErr("Report not found"); }
    })();
  }, [shareId]);

  if (err) return <div className="min-h-screen grid place-items-center text-stone-500">{err}</div>;
  if (!r) return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;

  const radarData = Object.entries(r.dimension_scores).map(([k, v]) => ({ dim: k, score: v }));
  const dealBreakers = r.ai?.deal_breakers || [];
  const growth = r.ai?.growth_trajectory;
  const firstConv = r.ai?.first_conversation || [];
  const elderLetter = r.ai?.elder_letter;
  const chem = r.ai?.chemistry_scores;

  const downloadPDF = () => {
    try {
      generateReportPDF(r, null);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] py-10">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 border border-amber-300 bg-amber-50 rounded-full px-3 py-1 text-xs text-amber-800">
            <span className="devanagari">अनुबंध</span> · Shared with family
          </div>
          <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1E1B4B] mt-4">Marriage Intelligence Report</h1>
          <div className="mt-2 text-stone-600">Compatibility with <strong>{r.name}</strong></div>
        </div>

        {/* Elder Letter — the most important thing for a family reader */}
        {elderLetter && (
          <div className="mt-8 rounded-2xl p-8 text-white relative overflow-hidden shadow-xl" style={{ background: "linear-gradient(135deg,#12103A,#1E1B4B)" }} data-testid="shared-elder-letter">
            <div className="flex items-center gap-2">
              <ScrollText className="text-amber-300" size={18} />
              <div className="overline" style={{ color: "#FCD34D" }}>A note for the elders</div>
            </div>
            <p className="mt-4 font-serif-display text-lg lg:text-xl italic leading-relaxed text-stone-100">
              &ldquo;{elderLetter}&rdquo;
            </p>
          </div>
        )}

        {/* Score summary */}
        <div className="mt-8 border border-stone-200 rounded-2xl bg-white p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="overline">Overall alignment</div>
              <div className="font-serif-display text-6xl text-amber-700 mt-2">{r.overall_score}%</div>
              <p className="mt-4 font-serif-display text-xl text-[#1E1B4B] leading-snug">{r.ai.headline}</p>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E7E5E4" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: "#57534E", fontSize: 11 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "#A8A29E", fontSize: 10 }} />
                  <Radar name="Alignment" dataKey="score" stroke="#D97706" fill="#D97706" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Deal-breakers — critical for family review */}
        {dealBreakers.length > 0 && (
          <div className="mt-6 rounded-2xl border-2 border-amber-400 bg-amber-50 p-6" data-testid="shared-deal-breakers">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-amber-700" size={18} />
              <div className="overline text-amber-800">Deal-Breaker Alerts</div>
            </div>
            <p className="text-sm text-stone-700 mt-2">Non-negotiable areas we recommend discussing openly.</p>
            <ul className="mt-4 space-y-3">
              {dealBreakers.map((d) => (
                <li key={`db-${d}`} className="flex gap-3 text-sm text-stone-800">
                  <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs grid place-items-center shrink-0 mt-0.5">!</span>
                  {d}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Growth trajectory */}
        {growth && (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="shared-growth-trajectory">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="text-emerald-700" size={18} />
                <div className="overline text-emerald-800">Growth Trajectory</div>
              </div>
              <div className="font-serif-display text-3xl text-emerald-800">{growth.score}%</div>
            </div>
            <p className="mt-2 font-serif-display text-lg text-[#1E1B4B] leading-snug">{growth.verdict}</p>
          </div>
        )}

        {/* Main narrative */}
        <div className="mt-6 border border-stone-200 rounded-2xl bg-white p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
            <div><div className="overline">Shared Strengths</div><ul className="mt-2 space-y-1 text-stone-700">{r.ai.shared_strengths.map((s) => <li key={`ss-${s}`}>◆ {s}</li>)}</ul></div>
            <div><div className="overline">Potential Challenges</div><ul className="mt-2 space-y-1 text-stone-700">{r.ai.potential_challenges.map((s) => <li key={`pc-${s}`}>◇ {s}</li>)}</ul></div>
            <div className="sm:col-span-2"><div className="overline">Recommended Conversations</div><ul className="mt-2 space-y-1 text-stone-700">{r.ai.recommended_conversations.map((s) => <li key={`rc-${s}`}>→ {s}</li>)}</ul></div>
            <div className="sm:col-span-2"><div className="overline">Family Fit</div><p className="mt-2 text-stone-700 leading-relaxed">{r.ai.family_fit}</p></div>
          </div>
        </div>

        {/* First conversation prompts */}
        {firstConv.length > 0 && (
          <div className="mt-6 border border-stone-200 rounded-2xl bg-white p-8" data-testid="shared-first-conversation">
            <div className="flex items-center gap-2">
              <MessageSquare className="text-amber-700" size={18} />
              <div className="overline">First 3 Questions</div>
            </div>
            <p className="text-sm text-stone-600 mt-2">Designed to be discussed with the elders present.</p>
            <ol className="mt-5 space-y-4">
              {firstConv.map((q, i) => (
                <li key={`fc-${i}`} className="flex gap-4">
                  <div className="font-serif-display text-3xl text-amber-600/70 leading-none shrink-0">{String(i + 1).padStart(2, "0")}</div>
                  <p className="text-[15px] text-[#1E1B4B] leading-relaxed pt-1">{q}</p>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Chemistry */}
        {chem && (
          <div className="mt-6 border border-stone-200 rounded-2xl bg-white p-8" data-testid="shared-chemistry">
            <div className="overline">Two of You — Chemistry</div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { key: "intellectual", label: "Intellectual" },
                { key: "emotional", label: "Emotional" },
                { key: "life_rhythm", label: "Life Rhythm" },
                { key: "values", label: "Values" },
              ].map((c) => (
                <div key={c.key} className="rounded-xl border border-stone-200 p-4">
                  <div className="text-xs text-stone-500 uppercase tracking-widest">{c.label}</div>
                  <div className="font-serif-display text-2xl text-[#1E1B4B] mt-1">{chem[c.key] || 0}%</div>
                  <div className="mt-2 h-1 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500" style={{ width: `${chem[c.key] || 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button
            data-testid="shared-download-pdf-btn"
            onClick={downloadPDF}
            className="btn-pill text-white px-6 py-3 text-sm flex items-center gap-2"
            style={{ background: "#1E1B4B" }}
          >
            <Download size={14} /> Download report as PDF
          </button>
          <Link
            to="/"
            className="btn-pill border border-stone-300 text-[#1E1B4B] px-6 py-3 text-sm hover:bg-stone-50"
          >
            Learn about Anubandh.ai
          </Link>
        </div>

        <p className="text-xs text-center text-stone-400 mt-8">
          Powered by Anubandh.ai · India&apos;s Marriage Intelligence Platform · Confidential family view
        </p>
      </div>
    </div>
  );
}
